import React, { useContext, useEffect, useState } from 'react'
import { BASE_URL_API } from '../../References/URLs'
import AuthContext from '../../store/auth-context'
import styles from './ManageEachEmployee.module.css'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Pen, Mail, Phone, Trash2, Copy, CheckCircle, Lock, Unlock, User, Archive, TriangleAlert, X } from 'lucide-react'
import CreateAuthCredentials from '../../Components/StaffManagement/CreateAuthCredentials'
import { toast } from 'react-toastify'


const ARCHIVE_REASONS = [
    'Employee resigned',
    'Terminated from employment',
    'Transferred to another branch',
    'Seasonal or temporary contract ended',
    'Duplicate or inactive employee record',
    'No longer associated with the salon',
]


const ManageEachEmployee = () => {
    const authCtx = useContext(AuthContext)
    const { employeeIdentifier } = useParams()
    const navigate = useNavigate()

    const [staffData, setStaffData] = useState(null)
    const [isAuthCreationOpen, setIsAuthCreationOpen] = useState(false)
    const [hasCopiedToClipboard, setHasCopiedToClipboard] = useState(false)
    const [togglingStatus, setTogglingStatus] = useState(false)
    const [deletingEmployee, setDeletingEmployee] = useState(false)
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
    const [archiveModalStep, setArchiveModalStep] = useState('impact')
    const [archiveReason, setArchiveReason] = useState('')
    const [archivingEmployee, setArchivingEmployee] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('')

    const employeeName = staffData?.name || 'this employee'
    const canConfirmDelete = deleteConfirmationText.trim().toLowerCase() === 'delete'

    const authCreationHandler = () => {
        setIsAuthCreationOpen(false)
    }
    const afterAuthCreation = () => {
        setIsAuthCreationOpen(false)
        toast("Successfully created the credentials!")
        getStaffDetails()
    }

    const getStaffDetails = async () => {
        const getStaffDetailsResponse = await fetch(BASE_URL_API + "getStaffDetails",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    staffIdentifier: employeeIdentifier
                })
            });

        if (!getStaffDetailsResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getStaffDetailsRespo = await getStaffDetailsResponse.json()
            if (getStaffDetailsRespo.status === "success") {
                setStaffData(getStaffDetailsRespo.response)
            } else {
                if (getStaffDetailsRespo.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    setStaffData(null)
                }
            }
            console.log(getStaffDetailsRespo)
        }
    }

    useEffect(() => {
        getStaffDetails()
    }, [employeeIdentifier])


    const copyToClipboard = () => {
        const textToCopy = `Dear ${staffData?.displayName}, 
                            I'm sharing you the login credentials for our CRM.
                            Use the details below to login into it.
                            URL : https://sm.lokaci.com
                            Username : ${staffData?.username}
                            Password : ${staffData?.password}
                            
                            Thanks`
        navigator.clipboard.writeText(textToCopy);
        setHasCopiedToClipboard(true)
        setTimeout(() => {
            setHasCopiedToClipboard(false)
        }, 4000);
    }

    const toggleStaffLogin = async () => {
        setTogglingStatus(true)
        const toggleStaffLoginResponse = await fetch(BASE_URL_API + "toggleStaffLogin",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    authIdentifier: staffData?.authIdentifier
                })
            });

        if (!toggleStaffLoginResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const toggleStaffLoginRespo = await toggleStaffLoginResponse.json()
            if (toggleStaffLoginRespo.status === "success") {
                toast(`Successfully updated the status`)
                getStaffDetails()
            } else {
                if (toggleStaffLoginRespo.message === "tokenExpired") {
                    authCtx.logout()
                } else {

                }
            }
            console.log(toggleStaffLoginRespo)
        }
        setTogglingStatus(false)
    }

    const openArchiveModal = () => {
        setArchiveModalStep('impact')
        setArchiveReason('')
        setIsArchiveModalOpen(true)
    }

    const closeArchiveModal = () => {
        if (archivingEmployee) return
        setIsArchiveModalOpen(false)
        setArchiveModalStep('impact')
        setArchiveReason('')
    }

    const proceedToArchiveReason = () => {
        setArchiveModalStep('reason')
    }

    const archiveStaff = async () => {
        if (!archiveReason) {
            toast.warn('Please select a reason for archiving this employee.')
            return
        }

        setArchivingEmployee(true)
        const archiveStaffResponse = await fetch(BASE_URL_API + 'archieveStoreEmployee',
            {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    staffIdentifier: employeeIdentifier,
                    archiveReason,
                    archieveReason: archiveReason,
                    reason: archiveReason,
                })
            })

        if (!archiveStaffResponse.ok) {
            toast.error('Unable to archive the employee right now.')
            console.log('Something went wrong : Server Error')
        } else {
            const archiveStaffRespo = await archiveStaffResponse.json()
            if (archiveStaffRespo.status === 'success') {
                toast.success('Employee archived successfully.')
                navigate('/manage/employees', { replace: true })
            } else if (archiveStaffRespo.message === 'tokenExpired') {
                authCtx.logout()
            } else {
                toast.error(archiveStaffRespo.message || 'Unable to archive the employee.')
            }
            console.log(archiveStaffRespo)
        }

        setArchivingEmployee(false)
    }

    const openDeleteModal = () => {
        setDeleteConfirmationText('')
        setIsDeleteModalOpen(true)
    }

    const closeDeleteModal = () => {
        if (deletingEmployee) return
        setDeleteConfirmationText('')
        setIsDeleteModalOpen(false)
    }

    const deleteStaff = async () => {
        if (!canConfirmDelete) {
            return
        }

        setDeletingEmployee(true)
        const deleteStaffResponse = await fetch(BASE_URL_API + "deleteStaff",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    staffIdentifier: employeeIdentifier
                })
            });

        if (!deleteStaffResponse.ok) {
            toast.error('Unable to delete the employee right now.')
            console.log("Something went wrong : Server Error")
        } else {
            const deleteStaffRespo = await deleteStaffResponse.json()
            if (deleteStaffRespo.status === "success") {
                toast.success('Employee deleted permanently.')
                navigate('/manage/employees', {replace: true})
            } else {
                if (deleteStaffRespo.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    toast.error(deleteStaffRespo.message || 'Unable to delete the employee.')
                }
            }
            console.log(deleteStaffRespo)
        }
        setDeletingEmployee(false)
        setIsDeleteModalOpen(false)
        setDeleteConfirmationText('')
    }


    return (
        <div className={styles.mainWrapper}>
            {isAuthCreationOpen && <CreateAuthCredentials isOpen={isAuthCreationOpen} onClose={authCreationHandler} onSuccess={afterAuthCreation} />}
            {isArchiveModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalCard}>
                        <div className={styles.modalHeader}>
                            <div className={`${styles.modalIconWrap} ${styles.archiveModalIcon}`}>
                                <Archive size={20} strokeWidth={2.2} />
                            </div>
                            <div className={styles.modalHeaderText}>
                                <h3>{archiveModalStep === 'impact' ? 'Archive employee?' : 'Select archive reason'}</h3>
                                <p>
                                    {archiveModalStep === 'impact'
                                        ? `Archive ${employeeName} so they no longer appear as active across the salon workflow.`
                                        : 'Choose the most accurate reason so the archive trail stays useful for your team.'}
                                </p>
                            </div>
                            <button type="button" className={styles.modalCloseBtn} onClick={closeArchiveModal} disabled={archivingEmployee}>
                                <X size={16} strokeWidth={2.2} />
                            </button>
                        </div>

                        {archiveModalStep === 'impact' ? (
                            <>
                                <div className={styles.modalAlertBox}>
                                    <TriangleAlert size={16} strokeWidth={2.2} />
                                    <span>This employee will be hidden from active operational surfaces, but the historical record remains intact.</span>
                                </div>
                                <div className={styles.modalImpactList}>
                                    <div className={styles.modalImpactItem}>Removed from active staff listings and day-to-day selection flows.</div>
                                    <div className={styles.modalImpactItem}>Historical bookings, transactions, and audit references remain preserved.</div>
                                    <div className={styles.modalImpactItem}>The employee can be reviewed later, but should no longer appear as actively working.</div>
                                </div>
                                <div className={styles.modalActions}>
                                    <button type="button" className={styles.modalSecondaryBtn} onClick={closeArchiveModal}>Cancel</button>
                                    <button type="button" className={styles.modalPrimaryBtn} onClick={proceedToArchiveReason}>Continue</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.reasonGrid}>
                                    {ARCHIVE_REASONS.map((reason) => (
                                        <button
                                            type="button"
                                            key={reason}
                                            className={`${styles.reasonCard} ${archiveReason === reason ? styles.reasonCardActive : ''}`}
                                            onClick={() => setArchiveReason(reason)}
                                        >
                                            <span className={styles.reasonTitle}>{reason}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.modalActions}>
                                    <button type="button" className={styles.modalSecondaryBtn} onClick={() => setArchiveModalStep('impact')} disabled={archivingEmployee}>Back</button>
                                    <button type="button" className={styles.modalArchiveBtn} onClick={archiveStaff} disabled={!archiveReason || archivingEmployee}>
                                        {archivingEmployee ? 'Archiving Employee...' : 'Confirm Archive'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {isDeleteModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={`${styles.modalCard} ${styles.deleteModalCard}`}>
                        <div className={styles.modalHeader}>
                            <div className={`${styles.modalIconWrap} ${styles.deleteModalIcon}`}>
                                <Trash2 size={20} strokeWidth={2.2} />
                            </div>
                            <div className={styles.modalHeaderText}>
                                <h3>Delete employee permanently?</h3>
                                <p>This action is irreversible and should only be used when you are absolutely certain.</p>
                            </div>
                            <button type="button" className={styles.modalCloseBtn} onClick={closeDeleteModal} disabled={deletingEmployee}>
                                <X size={16} strokeWidth={2.2} />
                            </button>
                        </div>

                        <div className={`${styles.modalAlertBox} ${styles.deleteAlertBox}`}>
                            <TriangleAlert size={16} strokeWidth={2.2} />
                            <span>Deleting {employeeName} will permanently remove the employee and the action cannot be undone.</span>
                        </div>

                        <div className={styles.archiveSuggestionBox}>
                            <Archive size={16} strokeWidth={2.2} />
                            <span>
                                If the employee has left the salon and you only want to hide them from active workflows,
                                choose <strong>Archive Employee</strong> instead of deleting.
                            </span>
                        </div>

                        <div className={styles.modalImpactList}>
                            <div className={styles.modalImpactItem}>Related services, transactions, and bookings tied to this employee may be impacted permanently.</div>
                            <div className={styles.modalImpactItem}>Operational and historical links associated with this person cannot be recovered after deletion.</div>
                            <div className={styles.modalImpactItem}>Use archive instead if you only want to make the employee inactive across active surfaces.</div>
                        </div>

                        <div className={styles.deleteConfirmFieldWrap}>
                            <label htmlFor="delete-employee-confirm" className={styles.deleteConfirmLabel}>Type <strong>delete</strong> to enable permanent deletion</label>
                            <input
                                id="delete-employee-confirm"
                                type="text"
                                value={deleteConfirmationText}
                                onChange={(event) => setDeleteConfirmationText(event.target.value)}
                                className={styles.deleteConfirmInput}
                                placeholder="Type delete"
                                autoComplete="off"
                                disabled={deletingEmployee}
                            />
                        </div>

                        <div className={styles.modalActions}>
                            <button type="button" className={styles.modalSecondaryBtn} onClick={closeDeleteModal} disabled={deletingEmployee}>Cancel</button>
                            <button type="button" className={styles.modalDangerBtn} onClick={deleteStaff} disabled={!canConfirmDelete || deletingEmployee}>
                                {deletingEmployee ? 'Deleting Permanently...' : 'Delete Permanently'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.mainContainer}>
                <div className={styles.mainContent}>
                    <div className={styles.pageHeader}>
                        <div className={styles.pageHeaderIcon}>
                            <User size={20} strokeWidth={2.3} />
                        </div>
                        <div className={styles.pageHeaderTitle}>
                            <h1>Employee Profile</h1>
                            <p>Manage staff information, credentials, and access</p>
                        </div>
                    </div>
                    <div className={styles.firstWrapper}>
                        <div className={styles.detailsWrapper}>
                            <div className={styles.detailsContainer}>
                                <div className={styles.name}>
                                    <div>
                                        {staffData?.name}
                                    </div>
                                    <Link to={`/staff/${employeeIdentifier}/profile`} className={styles.editIcon}>
                                        <Pen size={18} strokeWidth={2.5} />
                                    </Link>
                                </div>
                                <div className={styles.role}>
                                    {staffData?.role}
                                </div>
                                <div className={styles.mobile}>
                                    <Phone size={18} strokeWidth={2} />
                                    {staffData?.mobile ? staffData?.mobile : <span className={styles.missing}>No phone number added</span>}
                                </div>
                                <div className={styles.email}>
                                    <Mail size={18} strokeWidth={2} />
                                    {staffData?.email ? staffData?.email : <span className={styles.missing}>No email added</span>}
                                </div>
                            </div>
                        </div>

                        <div className={styles.authWrapper}>
                            {staffData?.hasAuthCreated === "false" ?
                                <div className={styles.authContainer}>
                                    <div className={styles.notCreatedTitle}>
                                        <h2>
                                            You havn't created Login credentials for this user.
                                        </h2>
                                    </div>
                                    <div className={styles.createBtnWrapper}>
                                        <button className={styles.createBtn} onClick={() => setIsAuthCreationOpen(true)} >Create Auth Credentials</button>
                                    </div>
                                </div>
                                :
                                <div className={styles.authContainer}>
                                    <div className={styles.createdTitle}>
                                        <h2>
                                            You have already created Login credentials for this user.
                                        </h2>
                                    </div>
                                    <div className={styles.tagWrapper}>
                                        {staffData?.isLoginEnabled === "1" ?
                                            <div className={styles.activeTag}>Active</div>
                                            :
                                            <div className={styles.disableTag}>Login Disabled</div>
                                        }
                                    </div>
                                    <div className={styles.credentailsWrapper}>
                                        <div className={styles.displayName}>
                                            <div className={styles.caption}>
                                                Display Name
                                            </div>
                                            <div className={styles.value}>
                                                {staffData?.displayName || <span className={styles.missing}>Not added yet</span>}
                                            </div>
                                        </div>
                                        <div className={styles.displayName}>
                                            <div className={styles.caption}>
                                                Username
                                            </div>
                                            <div className={styles.value}>
                                                {staffData?.username || <span className={styles.missing}>Not created yet</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.authActionWrapper}>
                                        <div className={styles.authActionContainer}>
                                            <Link to={`/manage-access/${staffData?.authIdentifier}`} className={styles.deactivateBtn} >Manage Access</Link>
                                            {togglingStatus === true ?
                                                <div className={styles.deactivateBtn} >
                                                    <Lock size={14} strokeWidth={2} />
                                                    Updating Status...
                                                </div>
                                                :
                                                <div className={styles.deactivateBtn} onClick={() => toggleStaffLogin()} >
                                                    {staffData?.isLoginEnabled === "1" ?
                                                        <>
                                                            <Unlock size={14} strokeWidth={2} />
                                                            Disable Login
                                                        </>
                                                        :
                                                        <>
                                                            <Lock size={14} strokeWidth={2} />
                                                            Enable Login
                                                        </>
                                                    }
                                                </div>
                                            }
                                            {hasCopiedToClipboard === true ?
                                                <div className={styles.credentialsCopied} onClick={() => copyToClipboard()} >
                                                    <CheckCircle size={14} strokeWidth={2} />
                                                    Copied To Share
                                                </div>
                                                :
                                                <div className={styles.copyCredentials} onClick={() => copyToClipboard()} >
                                                    <Copy size={14} strokeWidth={2} />
                                                    Copy To Share
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>

                    </div>

                    <div>
                        <div className={styles.actionWrapper}>
                            <button className={styles.archiveBtn} onClick={openArchiveModal} disabled={archivingEmployee || deletingEmployee}>
                                <Archive size={16} strokeWidth={2} />
                                {archivingEmployee ? 'Archiving Employee...' : 'Archive Employee'}
                            </button>
                            <button
                                className={styles.deleteBtn}
                                onClick={openDeleteModal}
                                title="This permanently deletes the employee and all impacted records cannot be recovered."
                                disabled={archivingEmployee || deletingEmployee}
                            >
                                <Trash2 size={16} strokeWidth={2} />
                                Delete Employee
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ManageEachEmployee